import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
import { brand, cardStyle, headStyle, EntPage } from './common';

/* Employee help page: the steps for standing up and maintaining an
 * Entrepreneur firm - the hub track every firm gets, and the optional Claude
 * track on top. A static reference that mirrors the live "Outstanding actions"
 * checklist each firm's page derives from its own state. */

// Every firm gets the hub. Claude is a separate, optional track on top of it -
// a firm (or any one member) can stay hub-only for good.
const HUB_STEPS = [
  {
    icon: 'building-add',
    title: 'Create the firm',
    body: 'On the Firms page, click New firm and enter its name. You’ll drop straight into the firm’s page to add its people.',
  },
  {
    icon: 'person-plus',
    title: 'Add members (sends hub invites)',
    body: 'On the firm’s page (Firms → the firm), add each person - or Bulk upload a spreadsheet from the Firms page. Each person gets a hub login invite with a temporary password. Make at least one an Admin so the firm can invite the rest of its team itself.',
  },
  {
    icon: 'megaphone',
    title: 'They’re in',
    body: 'Members see announcements, the Resource Library and training sign-ups straight away. Nothing about Claude is required for any of it.',
  },
];

const CLAUDE_STEPS = [
  {
    icon: 'clipboard-check',
    title: 'The firm applies for Claude - review & approve',
    body: 'A firm admin completes Claude Set Up in their portal (domains, limits, card). The firm then shows “Review application” under Claude → Claude Firms. Approve it on the firm’s Claude page (Claude → Claude Firms).',
  },
  {
    icon: 'globe',
    title: 'Confirm the domain in Claude',
    body: 'Whitelist the firm’s email domain in the Anthropic console, then click Mark domain verified on the firm’s Claude page.',
  },
  {
    icon: 'credit-card',
    title: 'Payment method on file',
    body: 'The firm adds a card during Claude Set Up; if needed you can add one under Billing on the firm’s Claude page. Seats can’t be charged until a card is on file.',
  },
  {
    icon: 'person-up',
    title: 'Seats are requested per member',
    body: 'Joining the hub does not request a seat. The firm admin (or you, from the member’s row on the firm’s page) requests a Claude seat for each person who should have one. Only those people are asked to complete Claude certification.',
  },
  {
    icon: 'person-check',
    title: 'Add members to Claude + enter cost',
    body: 'Once a requested member finishes Claude training, add them in the Anthropic console at their requested limit. Then click Confirm added to Claude (the firm’s Claude page, or Claude → Seat Requests) and enter the license cost - this charges the firm’s card. Use Withdraw to return a request to hub-only, or Reject to record that PCA declined it.',
  },
];

const MAINTENANCE = [
  {
    icon: 'sliders',
    title: 'Per-member spend limits',
    body: 'Firm admins set their own members’ monthly Claude spend limits, which push to Claude automatically via the Spend Limits API — no console step. Set the ceiling they can’t exceed on their own under Claude → Invoices → “Max per-member spend limit”.',
  },
  {
    icon: 'receipt',
    title: 'Invoice oversight',
    body: 'Monthly usage charges and one-time seat-activation charges are visible per firm on its Claude page, and across all firms under Claude → Invoices. To review and charge outstanding monthly usage by hand, use Claude → Charge Usage.',
  },
  {
    icon: 'arrow-counterclockwise',
    title: 'Refunds',
    body: 'Issue a full or partial refund on any paid charge from Billing on the firm’s Claude page. Refunds are recorded in the Actions Log.',
  },
];

function StepList({ steps, numbered }) {
  return (
    <div>
      {steps.map((s, i) => (
        <div key={s.title} style={{ display: 'flex', gap: '1rem', padding: '0.9rem 0', borderBottom: i < steps.length - 1 ? `1px solid ${brand.softGray}` : 'none' }}>
          <div style={{ flex: '0 0 auto', position: 'relative', width: 40, height: 40, borderRadius: 10, background: 'rgba(10,41,71,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={`bi bi-${s.icon}`} style={{ color: brand.deepBlue, fontSize: '1.1rem' }} />
            {numbered && (
              <span style={{ position: 'absolute', top: -6, left: -6, width: 20, height: 20, borderRadius: '50%', background: brand.goldenrod, color: 'white', fontSize: '0.7rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: brand.deepBlue }}>{s.title}</div>
            <div style={{ color: brand.charcoal, fontSize: '0.87rem', lineHeight: 1.5, marginTop: 2 }}>{s.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
StepList.propTypes = {
  steps: PropTypes.array.isRequired,
  numbered: PropTypes.bool,
};

export default function Info() {
  return (
    <EntPage title="How to set up an Entrepreneur firm" subtitle="A step-by-step reference: the hub every firm gets, then the optional Claude add-on.">
      <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
        <Card.Header style={headStyle}>1. Set up the firm&apos;s hub</Card.Header>
        <Card.Body>
          <p style={{ color: brand.charcoal, fontSize: '0.87rem' }}>
            Each firm&apos;s page shows an <strong>Outstanding actions</strong> list that highlights whichever of these
            steps is next for that firm - you don&apos;t have to remember the order.
          </p>
          <StepList steps={HUB_STEPS} numbered />
        </Card.Body>
      </Card>

      <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
        <Card.Header style={headStyle}>2. Claude (optional)</Card.Header>
        <Card.Body>
          <p style={{ color: brand.charcoal, fontSize: '0.87rem' }}>
            Only for firms that want Claude Enterprise seats. A firm that never signs up shows <strong>Claude: Not signed up</strong> on the Firms page - that is not a problem to chase.
          </p>
          <StepList steps={CLAUDE_STEPS} numbered />
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Header style={headStyle}>Ongoing maintenance</Card.Header>
        <Card.Body>
          <StepList steps={MAINTENANCE} />
        </Card.Body>
      </Card>
    </EntPage>
  );
}
